<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import {
  usePortalPermissionAdminStore,
  type PortalPermissionItem,
  type PortalPermissionDetail,
  type PortalPermissionCreatePayload,
  type PortalPermissionUpdatePayload,
  type PermissionSensitivity,
  type PermissionGrantedBy,
  type PermissionStatus,
} from '@/stores/usePortalPermissionAdminStore'
import {
  PlusIcon,
  GlobeAltIcon,
  UsersIcon,
  ShieldExclamationIcon,
  LockClosedIcon,
} from '@heroicons/vue/24/outline'

const store = usePortalPermissionAdminStore()

const showCreateModal = ref(false)
const saving = ref(false)
const errorMsg = ref<string | null>(null)

const showDetailModal = ref(false)
const detail = ref<PortalPermissionDetail | null>(null)
const isLoadingDetail = ref(false)
const updating = ref(false)
const updateError = ref<string | null>(null)

interface FormState {
  code: string
  label: string
  shortDescription: string
  longDescription: string
  whenToGrant: string
  whenNotToGrant: string
  category: string
  sensitivity: PermissionSensitivity
  grantedBy: PermissionGrantedBy
  status: PermissionStatus
  description: string
}

const createForm = ref<FormState>(buildEmptyForm())
const editForm = ref<FormState>(buildEmptyForm())

function buildEmptyForm(): FormState {
  return {
    code: '',
    label: '',
    shortDescription: '',
    longDescription: '',
    whenToGrant: '',
    whenNotToGrant: '',
    category: '',
    sensitivity: 'STANDARD',
    grantedBy: 'ACCOUNT_ADMIN_CLIENT',
    status: 'ACTIVE',
    description: '',
  }
}

function detailToForm(d: PortalPermissionDetail): FormState {
  return {
    code: d.code,
    label: d.label ?? '',
    shortDescription: d.shortDescription ?? '',
    longDescription: d.longDescription ?? '',
    whenToGrant: d.whenToGrant ?? '',
    whenNotToGrant: d.whenNotToGrant ?? '',
    category: d.category ?? '',
    sensitivity: d.sensitivity,
    grantedBy: d.grantedBy,
    status: d.status,
    description: d.description ?? '',
  }
}

function formToCreatePayload(f: FormState): PortalPermissionCreatePayload {
  return {
    code: f.code,
    label: f.label.trim() || null,
    shortDescription: f.shortDescription.trim() || null,
    longDescription: f.longDescription.trim() || null,
    whenToGrant: f.whenToGrant.trim() || null,
    whenNotToGrant: f.whenNotToGrant.trim() || null,
    category: f.category.trim() || null,
    sensitivity: f.sensitivity,
    grantedBy: f.grantedBy,
    status: f.status,
    description: f.description.trim() || null,
  }
}

function formToUpdatePayload(f: FormState): PortalPermissionUpdatePayload {
  return {
    label: f.label.trim() || null,
    shortDescription: f.shortDescription.trim() || null,
    longDescription: f.longDescription.trim() || null,
    whenToGrant: f.whenToGrant.trim() || null,
    whenNotToGrant: f.whenNotToGrant.trim() || null,
    category: f.category.trim() || null,
    sensitivity: f.sensitivity,
    grantedBy: f.grantedBy,
    status: f.status,
    description: f.description.trim() || null,
  }
}

const availableCodesNotUsed = computed(() => {
  const used = new Set(store.items.map((i) => i.code))
  return store.availableCodes
    .filter((c) => !used.has(c))
    .map((c) => ({ id: c, value: c, label: c }))
})

const sensitivityOptions = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'SENSITIVE', label: 'Sensible' },
] as const

const grantedByOptions = [
  { value: 'ACCOUNT_ADMIN_CLIENT', label: 'Admin du compte client' },
  { value: 'UNIXO_INTRANET', label: 'Intranet Unixo (vous)' },
] as const

const statusOptions = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'DEPRECATED', label: 'Dépréciée' },
  { value: 'UNUSED', label: 'Non utilisée' },
] as const

function statusBadgeClass(s: PermissionStatus) {
  return s === 'ACTIVE' ? 'badge-success' : s === 'DEPRECATED' ? 'badge-warning' : 'badge-ghost'
}

function openCreate() {
  createForm.value = buildEmptyForm()
  createForm.value.code = availableCodesNotUsed.value[0]?.value ?? ''
  errorMsg.value = null
  showCreateModal.value = true
}

function closeCreate() {
  showCreateModal.value = false
  errorMsg.value = null
}

async function submitCreate() {
  if (!createForm.value.code) {
    errorMsg.value = 'Sélectionne un code'
    return
  }
  saving.value = true
  errorMsg.value = null
  try {
    const res = await store.create(formToCreatePayload(createForm.value))
    if (res.ok) closeCreate()
    else errorMsg.value = res.message
  } finally {
    saving.value = false
  }
}

async function openDetail(item: PortalPermissionItem) {
  detail.value = null
  isLoadingDetail.value = true
  updateError.value = null
  showDetailModal.value = true
  const fetched = await store.fetchDetail(item.uuid)
  if (fetched) {
    detail.value = fetched
    editForm.value = detailToForm(fetched)
  }
  isLoadingDetail.value = false
}

function closeDetail() {
  showDetailModal.value = false
  detail.value = null
  updateError.value = null
}

async function saveDetail() {
  if (!detail.value) return
  updating.value = true
  updateError.value = null
  try {
    const res = await store.update(detail.value.uuid, formToUpdatePayload(editForm.value))
    if (res.ok) {
      // Sync detail state with returned data so contacts list reste cohérent
      detail.value = { ...detail.value, ...res.data }
    } else {
      updateError.value = res.message
    }
  } finally {
    updating.value = false
  }
}

function formatDate(s: string | null | undefined) {
  if (!s) return '—'
  return new Date(s).toLocaleString('fr-FR')
}

onMounted(async () => {
  await Promise.all([store.fetchAvailableCodes(), store.fetchAll()])
})
</script>

<template>
  <div class="p-6">
    <Bs2Title title="Portal permissions" subtitle="SUPER_ADMIN uniquement — référentiel + éditorial" />

    <div class="flex flex-wrap gap-2 mt-4">
      <router-link to="/dashboard/admin" class="btn btn-outline btn-sm">← Retour dashboard admin</router-link>
    </div>

    <p class="text-sm text-base-content/60 mt-4">
      Les codes sont définis par le code (enum DB). On peut ici activer un code disponible et saisir son éditorial
      (label utilisateur, descriptions, conseils d'attribution).
    </p>

    <div class="mt-6 card bg-base-100 shadow">
      <div class="card-body">
        <div class="flex items-center justify-between mb-4">
          <h2 class="card-title flex items-center gap-2">
            <GlobeAltIcon class="w-5 h-5" />
            Portal permissions ({{ store.items.length }} / {{ store.availableCodes.length }} codes)
          </h2>
          <div class="flex gap-2">
            <ButtonDaisy text="Actualiser" variant="outline" size="sm" @click="store.fetchAll()" />
            <button
              class="btn btn-primary btn-sm gap-1"
              :disabled="availableCodesNotUsed.length === 0"
              @click="openCreate"
            >
              <PlusIcon class="w-4 h-4" />
              Activer un code
            </button>
          </div>
        </div>

        <div v-if="store.isLoading" class="py-8 text-center">
          <span class="loading loading-spinner loading-lg" />
        </div>
        <div v-else-if="store.items.length === 0" class="py-8 text-center text-base-content/60">
          Aucune portal permission enregistrée. Activez un code parmi ceux disponibles.
        </div>
        <div v-else class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Code / Label</th>
                <th>Catégorie</th>
                <th>Sensibilité</th>
                <th>Attribué par</th>
                <th>Statut</th>
                <th class="text-center">Contacts</th>
                <th class="text-center">Usages</th>
                <th class="w-24"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in store.items" :key="p.uuid" class="hover">
                <td>
                  <div class="font-mono text-xs font-semibold">{{ p.code }}</div>
                  <div class="text-sm text-base-content/80">{{ p.label ?? '—' }}</div>
                </td>
                <td>
                  <span v-if="p.category" class="badge badge-ghost">{{ p.category }}</span>
                  <span v-else class="text-base-content/40">—</span>
                </td>
                <td>
                  <span
                    class="badge gap-1"
                    :class="p.sensitivity === 'SENSITIVE' ? 'badge-warning' : 'badge-ghost'"
                  >
                    <ShieldExclamationIcon v-if="p.sensitivity === 'SENSITIVE'" class="w-3 h-3" />
                    {{ p.sensitivity === 'SENSITIVE' ? 'Sensible' : 'Standard' }}
                  </span>
                </td>
                <td>
                  <span
                    class="badge gap-1"
                    :class="p.grantedBy === 'UNIXO_INTRANET' ? 'badge-info' : 'badge-ghost'"
                  >
                    <LockClosedIcon v-if="p.grantedBy === 'UNIXO_INTRANET'" class="w-3 h-3" />
                    {{ p.grantedBy === 'UNIXO_INTRANET' ? 'Intranet' : 'Admin client' }}
                  </span>
                </td>
                <td>
                  <span class="badge" :class="statusBadgeClass(p.status)">{{ p.status }}</span>
                </td>
                <td class="text-center">
                  <span class="badge badge-info">
                    <UsersIcon class="w-3 h-3 mr-1" />
                    {{ p.contactsCount }}
                  </span>
                </td>
                <td class="text-center tabular-nums text-sm">{{ p.usageCount }}</td>
                <td>
                  <button class="btn btn-ghost btn-xs" @click="openDetail(p)">Éditer</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal create -->
    <div class="modal" :class="{ 'modal-open': showCreateModal }">
      <div class="modal-box max-w-2xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <PlusIcon class="w-6 h-6" />
          Activer une portal permission
        </h3>

        <div class="mt-4 space-y-4">
          <SelectInput
            v-model="createForm.code"
            label="Code (enum)"
            :options="availableCodesNotUsed"
            placeholder="Sélectionner un code…"
          />

          <div class="form-control">
            <label class="label"><span class="label-text">Label utilisateur (UI-facing)</span></label>
            <input
              v-model="createForm.label"
              type="text"
              class="input input-bordered input-sm"
              placeholder="Ex: Voir tous les tickets de l'entreprise"
              :disabled="saving"
            />
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text">Description courte</span>
              <span class="label-text-alt">Affichée dans deniedMessage / tooltip</span>
            </label>
            <textarea
              v-model="createForm.shortDescription"
              class="textarea textarea-bordered textarea-sm"
              rows="2"
              :disabled="saving"
            />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="form-control">
              <label class="label"><span class="label-text">Catégorie</span></label>
              <input
                v-model="createForm.category"
                type="text"
                class="input input-bordered input-sm"
                placeholder="support, billing, services, account…"
                :disabled="saving"
              />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Sensibilité</span></label>
              <select v-model="createForm.sensitivity" class="select select-bordered select-sm" :disabled="saving">
                <option v-for="opt in sensitivityOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Attribué par</span></label>
              <select v-model="createForm.grantedBy" class="select select-bordered select-sm" :disabled="saving">
                <option v-for="opt in grantedByOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Statut</span></label>
              <select v-model="createForm.status" class="select select-bordered select-sm" :disabled="saving">
                <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>
          </div>

          <details class="collapse collapse-arrow bg-base-200">
            <summary class="collapse-title text-sm font-medium">Éditorial avancé (optionnel)</summary>
            <div class="collapse-content space-y-3">
              <div class="form-control">
                <label class="label"><span class="label-text">Description longue (ContactModal)</span></label>
                <textarea
                  v-model="createForm.longDescription"
                  class="textarea textarea-bordered textarea-sm"
                  rows="3"
                  :disabled="saving"
                />
              </div>
              <div class="form-control">
                <label class="label"><span class="label-text">Quand l'accorder</span></label>
                <textarea
                  v-model="createForm.whenToGrant"
                  class="textarea textarea-bordered textarea-sm"
                  rows="3"
                  placeholder="Profils, cas d'usage..."
                  :disabled="saving"
                />
              </div>
              <div class="form-control">
                <label class="label"><span class="label-text">Quand ne pas l'accorder</span></label>
                <textarea
                  v-model="createForm.whenNotToGrant"
                  class="textarea textarea-bordered textarea-sm"
                  rows="3"
                  :disabled="saving"
                />
              </div>
              <div class="form-control">
                <label class="label"><span class="label-text">Note interne (legacy)</span></label>
                <textarea
                  v-model="createForm.description"
                  class="textarea textarea-bordered textarea-sm"
                  rows="2"
                  :disabled="saving"
                />
              </div>
            </div>
          </details>

          <div v-if="errorMsg" class="alert alert-error text-sm">{{ errorMsg }}</div>
        </div>

        <div class="modal-action">
          <button class="btn" :disabled="saving" @click="closeCreate">Annuler</button>
          <button class="btn btn-primary" :disabled="saving || !createForm.code" @click="submitCreate">
            {{ saving ? 'Création…' : 'Créer' }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closeCreate" />
    </div>

    <!-- Modal detail / edit -->
    <div class="modal" :class="{ 'modal-open': showDetailModal }">
      <div class="modal-box max-w-4xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <GlobeAltIcon class="w-6 h-6" />
          <span class="font-mono">{{ detail?.code ?? '…' }}</span>
          <span v-if="detail?.label" class="text-sm text-base-content/60 font-normal">— {{ detail.label }}</span>
        </h3>

        <div v-if="isLoadingDetail" class="py-8 text-center">
          <span class="loading loading-spinner loading-lg" />
        </div>

        <div v-else-if="detail" class="mt-4 space-y-4">
          <!-- Edit form -->
          <div class="form-control">
            <label class="label"><span class="label-text">Label utilisateur</span></label>
            <input
              v-model="editForm.label"
              type="text"
              class="input input-bordered input-sm"
              :disabled="updating"
            />
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text">Description courte</span>
              <span class="label-text-alt">deniedMessage / tooltip</span>
            </label>
            <textarea
              v-model="editForm.shortDescription"
              class="textarea textarea-bordered textarea-sm"
              rows="2"
              :disabled="updating"
            />
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text">Description longue</span>
              <span class="label-text-alt">ContactModal admin client</span>
            </label>
            <textarea
              v-model="editForm.longDescription"
              class="textarea textarea-bordered textarea-sm"
              rows="3"
              :disabled="updating"
            />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="form-control">
              <label class="label"><span class="label-text">Quand l'accorder</span></label>
              <textarea
                v-model="editForm.whenToGrant"
                class="textarea textarea-bordered textarea-sm"
                rows="4"
                :disabled="updating"
              />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Quand ne pas l'accorder</span></label>
              <textarea
                v-model="editForm.whenNotToGrant"
                class="textarea textarea-bordered textarea-sm"
                rows="4"
                :disabled="updating"
              />
            </div>
          </div>

          <div class="grid grid-cols-4 gap-3">
            <div class="form-control">
              <label class="label"><span class="label-text">Catégorie</span></label>
              <input
                v-model="editForm.category"
                type="text"
                class="input input-bordered input-sm"
                :disabled="updating"
              />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Sensibilité</span></label>
              <select v-model="editForm.sensitivity" class="select select-bordered select-sm" :disabled="updating">
                <option v-for="opt in sensitivityOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Attribué par</span></label>
              <select v-model="editForm.grantedBy" class="select select-bordered select-sm" :disabled="updating">
                <option v-for="opt in grantedByOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">Statut</span></label>
              <select v-model="editForm.status" class="select select-bordered select-sm" :disabled="updating">
                <option v-for="opt in statusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>
          </div>

          <details class="collapse collapse-arrow bg-base-200">
            <summary class="collapse-title text-sm font-medium">Note interne (legacy)</summary>
            <div class="collapse-content">
              <textarea
                v-model="editForm.description"
                class="textarea textarea-bordered textarea-sm w-full"
                rows="2"
                :disabled="updating"
              />
            </div>
          </details>

          <div v-if="updateError" class="alert alert-error text-sm">{{ updateError }}</div>

          <!-- Usages (lecture seule, rempli par sync futur) -->
          <details class="collapse collapse-arrow bg-base-200">
            <summary class="collapse-title text-sm font-medium">
              Utilisations détectées
              <span class="text-base-content/60 ml-2">({{ detail.usageCount }})</span>
            </summary>
            <div class="collapse-content text-xs">
              <div v-if="detail.usageCount === 0" class="text-base-content/60 py-2">
                Aucun usage détecté. Sera rempli automatiquement par le sync du Feature Registry (à venir).
              </div>
              <pre v-else class="bg-base-100 p-3 rounded text-[10px] overflow-auto max-h-64">{{ JSON.stringify({
                api: detail.apiUsages,
                front: detail.frontUsages,
                routes: detail.routeUsages,
              }, null, 2) }}</pre>
            </div>
          </details>

          <!-- Contacts -->
          <div class="border-t pt-4">
            <h4 class="font-medium text-sm mb-2 flex items-center gap-2">
              <UsersIcon class="w-4 h-4" />
              Contacts ({{ detail.contacts.length }})
            </h4>
            <div v-if="detail.contacts.length === 0" class="text-sm text-base-content/60">
              Aucun contact n'a actuellement cette permission.
            </div>
            <div v-else class="overflow-x-auto max-h-72">
              <table class="table table-xs">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Société</th>
                    <th>Affecté le</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in detail.contacts" :key="`${c.contactUuid}-${c.companyUuid}`">
                    <td>{{ c.fullName }}</td>
                    <td>{{ c.email ?? '—' }}</td>
                    <td>{{ c.companyName ?? '—' }}</td>
                    <td>{{ formatDate(c.grantedAt) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="modal-action">
          <button class="btn" @click="closeDetail">Fermer</button>
          <button v-if="detail" class="btn btn-primary" :disabled="updating" @click="saveDetail">
            {{ updating ? 'Enregistrement…' : 'Enregistrer' }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closeDetail" />
    </div>
  </div>
</template>
